import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Select, Spin, message, Button } from 'antd';
import { useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../data/base';
import { CloseCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

/**
 * A simple component for selecting catalogs.
 * Minimal implementation to ensure multiple selection functionality.
 */
const SimpleCatalogSelect = ({ catalogsList = [], onChange, isFilterOpen = false }) => {
    const [loading, setLoading] = useState(false);
    const [catalogs, setCatalogs] = useState([]);
    const [selectedValues, setSelectedValues] = useState(['all']);

    // Usa un ref per tenere traccia degli aggiornamenti precedenti e prevenire loop
    const prevReduxCatalogsRef = useRef(null);
    const prevCatalogsListRef = useRef(null);

    // Ottieni i cataloghi da Redux in modo memoizzato per evitare nuovi riferimenti
    const reduxSelectedCatalogs = useSelector(state => state.catalogues.selectedCatalogs || []);

    // Memoizza la trasformazione per evitare di creare un nuovo array ad ogni render
    const reduxCatalogs = useMemo(() => {
        return reduxSelectedCatalogs.map(cat => ({
            id: cat.id,
            nameCatalog: cat.nameCatalog || cat.nome_brand || 'Unknown'
        }));
    }, [reduxSelectedCatalogs]);

    // Compara due array di cataloghi per vedere se sono effettivamente diversi
    const areCatalogsDifferent = (cats1, cats2) => {
        if (!cats1 || !cats2) return true;
        if (cats1.length !== cats2.length) return true;

        // Confronta solo gli IDs dei cataloghi
        const ids1 = cats1.map(cat => cat.id).sort();
        const ids2 = cats2.map(cat => cat.id).sort();

        return JSON.stringify(ids1) !== JSON.stringify(ids2);
    };

    // Effetto per caricare i cataloghi - eseguito solo quando catalogsList cambia realmente
    useEffect(() => {
        // Verifica se catalogsList è effettivamente cambiato
        if (!areCatalogsDifferent(catalogsList, prevCatalogsListRef.current)) {
            return;
        }

        prevCatalogsListRef.current = catalogsList;

        const fetchCatalogs = async () => {
            setLoading(true);
            try {
                if (!catalogsList || catalogsList.length === 0) {
                    setCatalogs([{ id: 'all', nameCatalog: 'All Catalogues' }]);
                    setLoading(false);
                    return;
                }

                const allPromises = catalogsList.map(async (id) => {
                    try {
                        const docRef = doc(db, 'Catalogues', id);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            // Filtra i cataloghi che sono Gateway catalogs
                            if (data.isGatewayCatalog === true) {
                                return null;
                            }
                            return {
                                id: docSnap.id,
                                nameCatalog: data.nameCatalog || 'Unnamed Catalog'
                            };
                        } else {
                            return {
                                id: id,
                                nameCatalog: `Catalog ${id.substring(0, 5)}...`
                            };
                        }
                    } catch (error) {
                        return {
                            id: id,
                            nameCatalog: `Catalog ${id.substring(0, 5)}...`
                        };
                    }
                });

                const results = await Promise.all(allPromises);
                const validCatalogs = results
                    .filter(c => c !== null)
                    .sort((a, b) => (a.nameCatalog || '').localeCompare(b.nameCatalog || ''));

                setCatalogs([
                    { id: 'all', nameCatalog: 'All Catalogues' },
                    ...validCatalogs
                ]);
            } catch (error) {
                message.error('Error loading catalogs');
            } finally {
                setLoading(false);
            }
        };

        fetchCatalogs();
    }, [catalogsList]); // Dipende solo da catalogsList

    // Effetto separato per sincronizzare con Redux - eseguito quando reduxCatalogs cambia realmente
    // Solo quando i filtri vengono applicati o resettati dall'esterno
    useEffect(() => {
        // Verifica se i cataloghi di Redux sono effettivamente cambiati
        if (!areCatalogsDifferent(reduxCatalogs, prevReduxCatalogsRef.current)) {
            return;
        }

        // Aggiorna il ref con i nuovi valori
        prevReduxCatalogsRef.current = reduxCatalogs;

        if (reduxCatalogs && reduxCatalogs.length > 0) {
            const newSelectedValues = reduxCatalogs.map(cat => cat.id);
            setSelectedValues(newSelectedValues);
        }
    }, [reduxCatalogs]);

    const handleChange = (values) => {
        // Se non ci sono valori selezionati, imposta 'all'
        if (values.length === 0) {
            values = ['all'];
        }

        // Rimuovi 'all' se sono selezionati anche altri valori
        if (values.includes('all') && values.length > 1) {
            values = values.filter(v => v !== 'all');
        }

        setSelectedValues(values);

        // Trova gli oggetti catalogo corrispondenti per passare anche i nomi
        const selectedCatalogObjects = values.map(id => {
            const catalog = catalogs.find(c => c.id === id);
            return catalog || { id, nameCatalog: `Catalog ${id.substring(0, 8)}...` };
        });

        // Non aggiornare Redux immediatamente - solo chiamare il callback
        // per notificare il componente parent (Filters) con gli oggetti completi
        if (onChange) onChange(values, selectedCatalogObjects);
    };

    // Function to reset selection to "All Catalogues"
    const resetToAll = () => {
        handleChange(['all']);
    };

    // Determine text to show for maxTagPlaceholder
    const getTagPlaceholder = () => {
        if (selectedValues.length === 1 && selectedValues[0] === 'all') {
            return 'All Catalogues';
        }
        if (selectedValues.length > 0) {
            return `${selectedValues.length} catalogs selected`;
        }
        return null;
    };

    // Render a simplified tag
    const SimpleTag = ({ label }) => (
        <span style={{
            padding: '0 7px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e8e8e8',
            borderRadius: '2px',
            marginRight: 4,
            display: 'inline-block',
            fontSize: 12,
            lineHeight: '20px',
            height: 22
        }}>
            {label}
        </span>
    );

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <Select
                mode="multiple"
                placeholder="Select catalogues"
                loading={loading}
                value={selectedValues}
                onChange={handleChange}
                style={{ width: '100%' }}
                notFoundContent={loading ? <Spin size="small" /> : null}
                optionFilterProp="children"
                maxTagCount={selectedValues.length === 1 && selectedValues[0] === 'all' ? 1 : 0}
                maxTagPlaceholder={getTagPlaceholder()}
                removeIcon={<span />} // Rimuove l'icona X dai tag
                tagRender={(props) => <SimpleTag label={props.label} />}
            >
                {catalogs.map(catalog => (
                    <Option key={catalog.id} value={catalog.id}>
                        {catalog.nameCatalog}
                    </Option>
                ))}
            </Select>
            {selectedValues.length > 0 && !(selectedValues.length === 1 && selectedValues[0] === 'all') && (
                <Button
                    type="text"
                    icon={<CloseCircleOutlined style={{ color: "grey" }} />}
                    onClick={resetToAll}
                    style={{
                        position: "absolute",
                        top: "50%",
                        right: "25px",
                        transform: "translateY(-50%)",
                        zIndex: 1,
                    }}
                />
            )}
        </div>
    );
};

export default SimpleCatalogSelect; 